import { useCallback } from 'react';
import { sessionToken } from './auth';
import { deviceAuthorization, discardDeviceCredential } from './launcher-device';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

/**
 * Talking to api.goodbuild.cc.
 *
 * The session travels as a bearer token rather than a cookie. That is not a
 * detail: the API sets `credentials: false` on its CORS policy precisely so a
 * cookie *cannot* be sent, which makes the whole surface immune to CSRF by
 * construction rather than by a check somebody has to remember to write.
 */
const BASE = import.meta.env['VITE_API_URL'] ?? 'https://api.goodbuild.cc';

/**
 * The API's origin, for the URLs a browser fetches directly rather than through
 * `request`.
 *
 * Today that is title art: `titleArtPath` in core returns a path and knows
 * nothing about hosts, because the API is `api.goodbuild.cc` in production, a
 * generated `workers.dev` hostname in dev and `localhost:8787` under
 * `pnpm dev`. An `<img src>` needs the whole thing.
 *
 * Exported rather than a second `import.meta.env` read elsewhere: two places
 * reading the same variable is two places to forget the fallback.
 */
export function apiOrigin(): string {
  return BASE.replace(/\/$/, '');
}

export class ApiError extends Error {
  override readonly name = 'ApiError';

  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

type TokenReader = () => Promise<string | null>;

/**
 * The `Authorization` value for this caller, whichever credential it holds.
 *
 * **The device token wins when there is one**, and that is the whole reason
 * this is a function rather than a template literal at the call site. A
 * launcher that has registered holds a long-lived, individually revocable
 * credential; a browser holds a session that expires. Preferring the session
 * inside the launcher would mean signing a player out every fortnight, which is
 * the thing device tokens exist to prevent.
 *
 * `deviceAuthorization` is synchronous and reads a cache filled before the
 * first render — see `launcher-device.ts` — because this runs on every request
 * and an IPC round trip per call would put the bridge in front of the whole
 * API. It answers null in a browser and null before that cache is filled, and
 * both fall through to the session, which authenticates either way.
 */
function authorization(sessionBearer: string | null): string | null {
  const device = deviceAuthorization();
  if (device !== null) return device;
  return sessionBearer === null ? null : `Bearer ${sessionBearer}`;
}

function send(path: string, header: string | null, init: RequestInit): Promise<Response> {
  return fetch(`${BASE}/v1${path}`, {
    ...init,
    headers: {
      ...(init.body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(header === null ? {} : { authorization: header }),
      ...init.headers,
    },
  });
}

async function request<T>(path: string, token: TokenReader, init: RequestInit = {}): Promise<T> {
  const sessionBearer = await token();
  const deviceWasUsed = deviceAuthorization() !== null;

  let response = await send(path, authorization(sessionBearer), init);

  if (response.status === 401 && deviceWasUsed) {
    await discardDeviceCredential();
    response = await send(path, authorization(sessionBearer), init);
  }

  if (!response.ok) {
    // The API always answers with `{ error }`, including on a 500. Falling back
    // to the status text keeps this honest if something upstream ever does not.
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(response.status, body?.error ?? response.statusText);
  }

  return (await response.json()) as T;
}

/**
 * A typed read.
 *
 * The session no longer needs waiting for. Under Clerk the SDK loaded
 * asynchronously and a query firing first got an anonymous 401 on every page
 * load; the token is read synchronously from storage now.
 *
 * `enabled` is back for a different reason: a path built from an answer that is
 * still out. `/portal/stats/:studioId` cannot be asked until `/me` has said
 * which studio, and the screens that need it used to fill the gap with the
 * literal string `none` — a request guaranteed to fail, whose failure was then
 * raced against the real one. Disabled, the query stays `isPending`, which is
 * the truthful state for a page that does not yet know what to ask about.
 */
export function useApi<T>(
  key: readonly unknown[],
  path: string,
  options: { enabled?: boolean } = {},
): UseQueryResult<T, ApiError> {
  return useQuery<T, ApiError>({
    queryKey: key,
    queryFn: () => request<T>(path, async () => sessionToken()),
    enabled: options.enabled ?? true,
  });
}

/**
 * A write. Returns a function rather than a hook result, for use in mutations.
 *
 * **Stable across renders, and that is load-bearing rather than an
 * optimisation.** It used to return a fresh closure every render, so every
 * `useMemo` and `useCallback` built on it was rebuilt every render too — which
 * is merely wasteful until something puts one in a `useEffect` dependency list,
 * at which point the effect re-runs on every render. The submission flow's
 * address lookup is exactly that: a debounced request whose timer would be
 * cleared and restarted forever, so it would never fire.
 *
 * `[]` is honest here. `request` and `sessionToken` are module scope and this
 * closes over nothing else, so there is nothing that could go stale.
 */
export function useApiMutation(): <T>(path: string, init: RequestInit) => Promise<T> {
  return useCallback(
    <T>(path: string, init: RequestInit) => request<T>(path, async () => sessionToken(), init),
    [],
  );
}
